import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Mail, MessageCircle, RefreshCw, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 100;

export default function MessageLogPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [channel, setChannel] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/crm/message-log`, {
        params: { channel: channel || undefined, q: q || undefined, limit: PAGE_SIZE, skip: page * PAGE_SIZE },
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      console.error('Versand-Protokoll laden fehlgeschlagen:', e);
    } finally {
      setLoading(false);
    }
  }, [channel, q, page]);

  useEffect(() => {
    const t = setTimeout(load, q ? 400 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString('de-CH', {
        day: '2-digit', month: '2-digit', year: '2-digit',
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return iso; }
  };

  const tabs = [
    { key: '', label: 'Alle' },
    { key: 'email', label: 'E-Mail' },
    { key: 'whatsapp', label: 'WhatsApp' },
  ];

  return (
    <div className="p-6 space-y-5" data-testid="message-log-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Send className="w-6 h-6" /> Versand-Protokoll
          </h1>
          <p className="text-sm text-gray-500">
            Alle automatisch versendeten E-Mails &amp; WhatsApps — {total} Einträge
          </p>
        </div>
        <Button variant="outline" onClick={load} data-testid="message-log-refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> Aktualisieren
        </Button>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex rounded-lg border overflow-hidden">
          {tabs.map((t) => (
            <button
              key={t.key}
              data-testid={`message-log-tab-${t.key || 'all'}`}
              onClick={() => { setChannel(t.key); setPage(0); }}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                channel === t.key ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <Input
          placeholder="Suche: Empfänger, Betreff, Quelle …"
          value={q}
          onChange={(e) => { setQ(e.target.value); setPage(0); }}
          className="max-w-xs"
          data-testid="message-log-search"
        />
      </div>

      <div className="border rounded-xl overflow-x-auto bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 border-b bg-gray-50">
              <th className="py-2.5 px-4 whitespace-nowrap">Zeit</th>
              <th className="py-2.5 px-4">Kanal</th>
              <th className="py-2.5 px-4">Empfänger</th>
              <th className="py-2.5 px-4">Betreff / Inhalt</th>
              <th className="py-2.5 px-4">Quelle</th>
              <th className="py-2.5 px-4">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="6" className="py-10 text-center text-gray-400">Lade …</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan="6" className="py-10 text-center text-gray-400" data-testid="message-log-empty">
                Noch keine Einträge — jeder neue automatische Versand erscheint hier.
              </td></tr>
            ) : items.map((m) => (
              <tr key={m.id} className="border-b last:border-0 hover:bg-gray-50" data-testid="message-log-row">
                <td className="py-2.5 px-4 whitespace-nowrap text-gray-600">{fmt(m.sent_at)}</td>
                <td className="py-2.5 px-4">
                  {m.channel === 'whatsapp' ? (
                    <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                      <MessageCircle className="w-3 h-3 mr-1" /> WhatsApp
                    </Badge>
                  ) : (
                    <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">
                      <Mail className="w-3 h-3 mr-1" /> E-Mail
                    </Badge>
                  )}
                </td>
                <td className="py-2.5 px-4 font-medium">{m.recipient}</td>
                <td className="py-2.5 px-4 text-gray-700 max-w-md truncate" title={m.subject}>{m.subject}</td>
                <td className="py-2.5 px-4 text-gray-500">{m.source}</td>
                <td className="py-2.5 px-4">
                  {m.status === 'sent' ? (
                    <Badge className="bg-green-100 text-green-700 hover:bg-green-100">gesendet</Badge>
                  ) : (
                    <Badge className="bg-red-100 text-red-700 hover:bg-red-100" title={m.error}>fehlgeschlagen</Badge>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {total > PAGE_SIZE && (
        <div className="flex items-center gap-3 justify-end text-sm">
          <Button variant="outline" size="sm" disabled={page === 0}
            onClick={() => setPage((p) => p - 1)} data-testid="message-log-prev">Zurück</Button>
          <span className="text-gray-500">Seite {page + 1} / {Math.ceil(total / PAGE_SIZE)}</span>
          <Button variant="outline" size="sm" disabled={(page + 1) * PAGE_SIZE >= total}
            onClick={() => setPage((p) => p + 1)} data-testid="message-log-next">Weiter</Button>
        </div>
      )}
    </div>
  );
}
